"use client";

// "How it works" (design handoff §7): three step cards whose visual panels
// play one shared 11.5s loop, driven from requestAnimationFrame and written
// straight to the DOM (no React state, so nothing re-renders per frame).

import { Check } from "iconoir-react";
import * as React from "react";

import { ArrowPillLink } from "@/components/ui/arrow-pill-link";
import { LOCALE_CURRENCY } from "@/lib/locale-prefix";
import { cn } from "@/lib/utils";

import {
  CONNECTION_COUNT,
  getTimelineFrame,
  LOOP_SECONDS,
  REST_SECONDS,
} from "./how-it-works-timeline";
import { LoopVideo } from "./loop-video";

import styles from "./working-capital.module.css";

interface HowItWorksCopy {
  title: string;
  ctaLabel: string;
  ctaHref: string;
  steps: { title: string; description: string }[];
  connectionLabels: [string, string, string, string];
  connecting: string;
  connected: string;
  limitLabel: string;
  limitNotes: { basedOnData: string; grows: string };
  /** Accessible description of the step 3 "draw down" video. */
  drawVideoTitle: string;
}

interface HowItWorksProps {
  copy: HowItWorksCopy;
  locale: string;
  className?: string;
}

// Mux asset for the step 3 "draw down" panel loop (1920x1268, public). It is a
// recording of the panel itself, rounded corners and border included.
const DRAW_DOWN_VIDEO_PLAYBACK_ID =
  "h6iFpBR8zBqdffNTRUCBnNdMh32Nb00By65vz8Vt9vo4";

const PANEL = "rounded-ds-l h-[240px] overflow-hidden";

export function HowItWorks({ copy, locale, className }: HowItWorksProps) {
  const root = React.useRef<HTMLDivElement>(null);

  const format = React.useMemo(() => {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: LOCALE_CURRENCY[locale] ?? "USD",
      maximumFractionDigits: 0,
    });
    return (n: number) => formatter.format(n);
  }, [locale]);

  React.useEffect(() => {
    const el = root.current;
    if (!el) return;

    const q = <T extends HTMLElement>(sel: string) =>
      el.querySelector<T>(`[data-hiw="${sel}"]`);
    const qAll = (sel: string) =>
      Array.from(el.querySelectorAll<HTMLElement>(`[data-hiw="${sel}"]`));

    const panels = qAll("panel");
    const dots = qAll("dot");
    const checks = qAll("check");
    const statuses = qAll("status");
    const limitAmount = q("limit-amount");
    const limitBar = q("limit-bar");
    const limitNote = q("limit-note");

    const dotColors = ["#e5e6e6", "#001eff", "#3fa95b"];

    const apply = (seconds: number) => {
      const f = getTimelineFrame(seconds);
      for (const p of panels) p.style.opacity = String(f.panelOpacity);
      f.connections.forEach((state, i) => {
        const dot = dots[i];
        const check = checks[i];
        const status = statuses[i];
        if (dot) dot.style.backgroundColor = dotColors[state] ?? "";
        if (check) check.style.opacity = state === 2 ? "1" : "0";
        if (status) {
          status.textContent =
            state === 0 ? "" : state === 1 ? copy.connecting : copy.connected;
        }
      });
      if (limitAmount) limitAmount.textContent = format(f.limit);
      if (limitBar) limitBar.style.width = `${f.limitBarPercent}%`;
      if (limitNote) limitNote.textContent = copy.limitNotes[f.limitNote];
    };

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduced.matches) {
      apply(REST_SECONDS);
      return;
    }

    let frameId = 0;
    let visible = false;
    let started = 0;

    const tick = (now: number) => {
      if (!started) started = now;
      apply(((now - started) / 1000) % LOOP_SECONDS);
      frameId = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      const next = Boolean(entry?.isIntersecting);
      if (next === visible) return;
      visible = next;
      if (visible) {
        started = 0;
        frameId = requestAnimationFrame(tick);
      } else {
        cancelAnimationFrame(frameId);
      }
    });
    observer.observe(el);
    apply(0);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameId);
    };
  }, [copy, format]);

  const connectionRows = copy.connectionLabels.slice(0, CONNECTION_COUNT);

  return (
    <section
      id="how-it-works"
      data-testid="working-capital-how-it-works"
      className={cn(
        "bg-grey-0 scroll-mt-[120px] px-6 py-16 md:px-8 lg:px-20 lg:py-24",
        className
      )}
    >
      <div
        ref={root}
        className="mx-auto flex max-w-[1280px] flex-col gap-10 lg:gap-14"
      >
        {/* 17.5em: wide enough for "Set up your account in minutes, then", too narrow for "access". */}
        <h2 className="text-ds-heading-h3 md:text-ds-display-sm text-grey-950 max-w-[17.5em]">
          {copy.title}
        </h2>

        <ol className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-12">
          {copy.steps.map((step, index) => (
            <li
              key={step.title}
              className="rounded-ds-xl border-grey-100 bg-grey-50 relative flex flex-col gap-5 border p-4"
            >
              {index === 0 ? (
                <div
                  data-hiw="panel"
                  className={cn(
                    PANEL,
                    "border-grey-100 bg-grey-0 flex flex-col gap-2 border p-5"
                  )}
                >
                  {connectionRows.map((label) => (
                    <div
                      key={label}
                      className="rounded-ds-l border-grey-100 flex h-10 items-center gap-2.5 border px-3"
                    >
                      <span
                        data-hiw="dot"
                        className="relative flex size-[18px] items-center justify-center rounded-full bg-[#e5e6e6] transition-colors duration-[250ms]"
                      >
                        <Check
                          data-hiw="check"
                          aria-hidden
                          className="text-grey-0 size-3 opacity-0 transition-opacity duration-200"
                        />
                      </span>
                      <span className="text-[13px] leading-[1.4]">{label}</span>
                      <span
                        data-hiw="status"
                        className="text-grey-500 ml-auto text-[12px]"
                      />
                    </div>
                  ))}
                </div>
              ) : index === 1 ? (
                <div
                  data-hiw="panel"
                  className={cn(
                    PANEL,
                    "relative flex flex-col justify-between bg-[#000633] p-5 text-white"
                  )}
                >
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage:
                        "url(/images/home/funding-estimate-gradient.png)",
                    }}
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 opacity-40 mix-blend-overlay"
                    style={{
                      backgroundImage: "url(/images/noise-texture.png)",
                    }}
                  />
                  <p className="relative text-[12px]">{copy.limitLabel}</p>
                  <div className="relative flex flex-col gap-3">
                    <p
                      data-hiw="limit-amount"
                      className="text-[48px] leading-[1.1] tracking-[-0.04em] tabular-nums"
                    />
                    <div className="h-1 rounded-full bg-white/20">
                      <div
                        data-hiw="limit-bar"
                        className="h-full w-0 rounded-full bg-white"
                      />
                    </div>
                    <p data-hiw="limit-note" className="text-[12px]" />
                  </div>
                </div>
              ) : (
                <LoopVideo
                  playbackId={DRAW_DOWN_VIDEO_PLAYBACK_ID}
                  title={copy.drawVideoTitle}
                  objectPosition="center"
                  backgroundColor="#ffffff"
                  // The clip is 1920x1268: below 536px size the panel to it so
                  // the recording isn't cropped into the fixed 240px frame.
                  className={cn(
                    "rounded-ds-l aspect-[1920/1268] w-full overflow-hidden min-[536px]:aspect-auto min-[536px]:h-[240px]"
                  )}
                />
              )}

              <div className="flex flex-col gap-3 px-2 pt-1 pb-2">
                <h3 className="text-ds-heading-h5 text-grey-950 leading-[1.3]">
                  <span className="text-grey-500">
                    {String(index + 1).padStart(2, "0")}.{" "}
                  </span>
                  {step.title}
                </h3>
                <p className="text-ds-body-md text-grey-500 leading-[1.5]">
                  {step.description}
                </p>
              </div>

              {index < copy.steps.length - 1 && (
                <div
                  aria-hidden
                  className="border-grey-400 absolute top-[136px] -right-12 hidden h-px w-12 border-t border-dashed lg:block"
                >
                  <svg
                    viewBox="0 0 8 10"
                    className="absolute -top-[5px] -right-px h-2.5 w-2"
                    fill="none"
                    stroke="#b0b3b5"
                    strokeWidth="1"
                  >
                    <path d="m1 1 5 4-5 4" strokeLinecap="round" />
                  </svg>
                  <span
                    className={cn(
                      "bg-pulse-700 absolute -top-[2.5px] left-0 size-[5px] rounded-full",
                      styles["flowDot"]
                    )}
                  />
                </div>
              )}
            </li>
          ))}
        </ol>

        <div className="flex justify-center">
          <ArrowPillLink href={copy.ctaHref}>{copy.ctaLabel}</ArrowPillLink>
        </div>
      </div>
    </section>
  );
}
